/**
 * Status-aware calling — shared eligibility evaluator.
 *
 * Contactability model (user-confirmed): a user is essentially ALWAYS
 * contactable. Presence (offline/idle) alone never blocks and never warns — we
 * only WARN when we have a positive signal the target is being contacted outside
 * their stated availability (or has DND / is on another call). We decide to:
 *  - `allow`  — place the call immediately (no friction),
 *  - `warn`   — let the caller proceed but surface a "contact anyway?" heads-up
 *               (outside availability, do-not-disturb, on another call, out/away
 *               by manual status), or
 *  - `block`  — reserved; defaults never block (kept so ops can re-enable a hard
 *               gate via config if ever needed). Urgent / on-call rings bypass
 *               the gate entirely upstream.
 *
 * Availability-aware: an OFFLINE target with no availability set, or whose
 * availability says they're available now, is contactable with NO warning. Only
 * an availability calendar that resolves to `unavailable` produces the
 * outside-availability warning — regardless of presence.
 *
 * This mirrors the transfer-eligibility gate model so both flows stay tunable
 * from one place. Pure + framework-agnostic so it can be unit-tested and shared.
 */

import type { AvailabilityCalendar } from '../availability/types'
import { resolveAvailabilityAt } from '../availability/resolve'

export type CallGateSeverity = 'block' | 'warn' | 'allow'

export type CallEligibilityConfig = {
  gates: {
    /** Target presence derived OFFLINE (unseen 30m+ or signed out). */
    targetOffline: CallGateSeverity
    /** Target presence derived IDLE (5–30m since last activity). */
    targetIdle: CallGateSeverity
    /** Target manual activity has pauseNotifications (do-not-disturb). */
    targetPauseNotifications: CallGateSeverity
    /** Target already on an active voice/video call on any device. */
    targetOnActiveCall: CallGateSeverity
    /** Target availability calendar resolves to unavailable right now. */
    targetCalendarUnavailable: CallGateSeverity
    /** Per manual activity preset id (e.g. focus-time, out-sick). */
    activityPreset: Partial<Record<string, CallGateSeverity>>
  }
}

export const DEFAULT_CALL_ELIGIBILITY: CallEligibilityConfig = {
  gates: {
    // Presence never gates contactability: offline/idle alone is allowed and
    // silent. Availability (below) drives the only presence-adjacent warning.
    targetOffline: 'allow',
    targetIdle: 'allow',
    // DND / on-another-call are real "may not answer" heads-ups, but never block.
    targetPauseNotifications: 'warn',
    targetOnActiveCall: 'warn',
    // Outside the target's stated availability → "contact anyway?" warning.
    targetCalendarUnavailable: 'warn',
    activityPreset: {
      'out-sick': 'warn',
      vacation: 'warn',
      'in-meeting': 'warn',
      'focus-time': 'warn',
      commuting: 'warn',
      'working-remotely': 'allow',
    },
  },
}

export type CallTargetContext = {
  userId: string
  displayName?: string
  /** Derived presence: ACTIVE | IDLE | OFFLINE */
  presence?: string | null
  /** Manual/AHP activity preset id when set. */
  activityPresetId?: string | null
  /** Manual activity pauseNotifications flag. */
  pauseNotifications?: boolean
  /** True when target has an active voice/video call on any device. */
  onActiveCall?: boolean
  /** Parsed availability calendar from target profile settings. */
  availabilityCalendar?: AvailabilityCalendar | null
}

export type CallEligibilityIssue = {
  code: string
  severity: Exclude<CallGateSeverity, 'allow'>
  message: string
}

export type CallEligibilityResult = {
  /** Highest severity across all issues ('allow' when none apply). */
  severity: CallGateSeverity
  /** True unless any issue is `block`. */
  canCall: boolean
  issues: CallEligibilityIssue[]
}

function severityRank(severity: CallGateSeverity): number {
  if (severity === 'block') return 2
  if (severity === 'warn') return 1
  return 0
}

function applyGate(
  issues: CallEligibilityIssue[],
  code: string,
  severity: CallGateSeverity,
  message: string,
): void {
  if (severity === 'allow') return
  issues.push({ code, severity, message })
}

/**
 * Evaluate whether a 1:1 call target is reachable. Aggregates block/warn issues;
 * `severity` is the highest issue severity and `canCall` is false only when a
 * `block` applies.
 */
export function evaluateCallEligibility(
  target: CallTargetContext,
  config: CallEligibilityConfig = DEFAULT_CALL_ELIGIBILITY,
): CallEligibilityResult {
  const issues: CallEligibilityIssue[] = []
  const name = target.displayName?.trim() || 'This user'
  const gates = config.gates

  const presence = (target.presence ?? '').toUpperCase()

  if (presence === 'OFFLINE') {
    applyGate(issues, 'target_offline', gates.targetOffline, `${name} is offline right now.`)
  } else if (presence === 'IDLE') {
    applyGate(issues, 'target_idle', gates.targetIdle, `${name} is away and may not answer.`)
  }

  if (target.pauseNotifications) {
    applyGate(
      issues,
      'target_pause_notifications',
      gates.targetPauseNotifications,
      `${name} has notifications paused (do not disturb).`,
    )
  }

  if (target.onActiveCall) {
    applyGate(
      issues,
      'target_on_active_call',
      gates.targetOnActiveCall,
      `${name} is already on another call.`,
    )
  }

  if (target.activityPresetId) {
    const severity = gates.activityPreset[target.activityPresetId] ?? 'allow'
    applyGate(
      issues,
      `activity_${target.activityPresetId}`,
      severity,
      `${name} is marked ${target.activityPresetId.replace(/-/g, ' ')}.`,
    )
  }

  if (target.availabilityCalendar) {
    const resolved = resolveAvailabilityAt(target.availabilityCalendar)
    if (resolved === 'unavailable') {
      applyGate(
        issues,
        'target_calendar_unavailable',
        gates.targetCalendarUnavailable,
        `You're contacting ${name} outside their availability — contact anyway?`,
      )
    }
  }

  const severity: CallGateSeverity = issues.some((i) => i.severity === 'block')
    ? 'block'
    : issues.some((i) => i.severity === 'warn')
      ? 'warn'
      : 'allow'

  return { severity, canCall: severity !== 'block', issues }
}

/** Pick the highest-severity issue for headline copy. */
export function primaryCallEligibilityIssue(
  result: CallEligibilityResult,
): CallEligibilityIssue | null {
  if (result.issues.length === 0) return null
  return [...result.issues].sort(
    (a, b) => severityRank(b.severity) - severityRank(a.severity),
  )[0]
}
